import { t } from '@lingui/core/macro';

import { Display, Flex, Text } from '@rover/kibble/core';
import NewServiceBadge from '@rover/react-lib/src/pages/grooming/components/NewServiceBadge';
import useInGroomingPilot from '@rover/react-lib/src/pages/grooming/hooks/useInGroomingPilot';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import type { BrandCountryServices } from '@rover/shared/js/hooks/useLocalizedBrandCountryServicesByServiceType';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';

import getServiceOverviewCategories from './getServiceOverviewCategories';
import ServiceCategoryTabs from './ServiceCategoryTabs';

type Props = {
  servicesByType: BrandCountryServices | null;
  hasPetInsurance?: boolean;
  defaultActiveTabId?: string;
};

interface ServiceOverviewHeaderContent {
  header: string;
  subheader: string;
}

const getServiceOverviewHeaderContent = (): ServiceOverviewHeaderContent =>
  getBrandSpecificValue<ServiceOverviewHeaderContent>({
    rover: {
      header: t`Get to know services on Rover`,
      subheader: t`Get the care your pet needs, anytime and anywhere.`,
    },
  });

const ServiceOverview = ({
  servicesByType,
  hasPetInsurance,
  defaultActiveTabId,
}: Props): JSX.Element => {
  const { header, subheader } = getServiceOverviewHeaderContent();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();
  const { shouldShowTrainingEntrypoints } = useInTrainingPilot();

  const serviceOverviewCategories = getServiceOverviewCategories(servicesByType, {
    hasPetInsurance,
    shouldShowGrooming: shouldShowGroomingEntrypoints,
    shouldShowTraining: shouldShowTrainingEntrypoints,
  }).map((tab) => ({
    ...tab,
    icon: tab.id === 'other' && shouldShowGroomingEntrypoints ? NewServiceBadge : undefined,
  }));

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap="0x"
      mb="4x"
      paddingTop={['8x', '12x']}
    >
      <Display size="400" mb="1x">
        {header}
      </Display>
      <Text mb="8x">{subheader}</Text>
      <ServiceCategoryTabs
        services={serviceOverviewCategories}
        defaultActiveTabId={defaultActiveTabId}
      />
    </Flex>
  );
};

export default ServiceOverview;
